import React, { useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPersonChalkboard, faSoccerBall, faStar, faTrophy, faCompress, faExpand } from '@fortawesome/free-solid-svg-icons';
import { GameState, Player, DisplayFixture, League, TeamColors, MatchEvent } from '../../../domain/entities';
import { View } from '../../../App';
import { soundService } from '../../../services/SoundService';
import { getThemeReadableAccentColor } from '../../../utils/theme';
import { calculateZoneStrengths } from '../../../services/MatchAnalysisService';
import { ASSISTANT_MANAGER_LEVELS } from '../../../domain/constants';
import { MiniBalanceOfPlay } from '../../shared/MiniBalanceOfPlay';
import { PlayerFace } from '../../shared/PlayerFace';
import { FormDisplay } from '../../shared/FormDisplay';
import { TeamBadge } from '../../shared/TeamBadge';
import { getLeagueColor } from '../../../utils/iconUtils';
import { FixturesHelper } from '../../../services/FixturesHelper';
import { usePanelViewPreferences } from '../../../hooks/usePanelViewPreferences';

interface LastResultPanelProps {
    lastFixture: DisplayFixture | undefined;
    gameState: GameState;
    getPlayerById: (id: number) => Player | undefined;
    pushView: (view: View) => void;
    onFixtureClick: (fixture: DisplayFixture) => void;
}

const getCompetitionEmoji = (competition: string, gameState: GameState) => {
    const cupNames = gameState.config.CUP_NAMES;
    if (cupNames.includes(competition)) {
        return competition === cupNames[0] ?
            <FontAwesomeIcon icon={faTrophy} style={{ color: '#FFD700', marginRight: '4px' }} /> : // Pixel Cup - gold
            <FontAwesomeIcon icon={faTrophy} style={{ color: '#C0C0C0', marginRight: '4px' }} />; // Junior Cup - silver
    }
    // League - use trophy with tier-based color
    return <FontAwesomeIcon icon={faTrophy} style={{ color: getLeagueColor(competition, gameState.config.LEAGUE_NAMES), marginRight: '4px' }} />;
};

const getResultClass = (fixture: DisplayFixture, subjectTeamName: string): string => {
    if (!fixture.result) return '';
    const { home, away, result } = fixture;
    const { homeScore, awayScore } = result;

    if (homeScore === awayScore) return 'result-draw';
    if (home === subjectTeamName) {
        return homeScore > awayScore ? 'result-win' : 'result-loss';
    }
    if (away === subjectTeamName) {
        return awayScore > homeScore ? 'result-win' : 'result-loss';
    }
    return '';
};

const getResultText = (fixture: DisplayFixture): string => {
    if (!fixture.result) return '';
    const { home, away, result } = fixture;
    const { homeScore, awayScore } = result;

    if (homeScore === awayScore) return 'Draw';
    if (homeScore > awayScore) return `Home Win (${home})`;
    return `Away Win (${away})`;
};

const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
};

const generateMatchAnalysis = (fixture: DisplayFixture, playerTeamName: string, accuracy: number): string => {
    if (!fixture.result) return '';

    const { homeScore, awayScore, events } = fixture.result;
    const isWin = (fixture.home === playerTeamName && homeScore > awayScore) || (fixture.away === playerTeamName && awayScore > homeScore);
    const isDraw = homeScore === awayScore;

    const playerTeamGoals = events.filter(e => e.type === 'goal' && e.team === playerTeamName).length;
    const opponentGoals = events.filter(e => e.type === 'goal' && e.team !== playerTeamName).length;
    const yellowCards = events.filter(e => e.type === 'yellow_card').length;
    const redCards = events.filter(e => e.type === 'red_card').length;

    if (accuracy >= 0.85) {
        // Detailed analysis
        let analysis = '';

        if (isWin) {
            analysis = `Strong performance. You dominated with ${playerTeamGoals} goals against ${opponentGoals}. `;
            if (playerTeamGoals >= 3) {
                analysis += `Excellent finishing. Your team was clinical in front of goal. `;
            } else if (playerTeamGoals === 2) {
                analysis += `Solid attacking play throughout the match. `;
            }
        } else if (isDraw) {
            analysis = `Competitive match. Both teams struggled to break through. ${playerTeamGoals} goals each shows balanced play. `;
            if (yellowCards > 2) {
                analysis += `High intensity but some discipline issues. `;
            }
        } else {
            analysis = `Disappointing result. You conceded ${opponentGoals} while only scoring ${playerTeamGoals}. `;
            if (opponentGoals >= 3) {
                analysis += `Defensive vulnerabilities cost you this match. Focus on your back line. `;
            } else {
                analysis += `Your attack was blunt when you needed to convert chances. `;
            }
        }

        if (redCards > 0) {
            analysis += `Note: You received ${redCards} red card(s) which impacted play. `;
        }

        analysis += `Review your tactical setup for next time.`;
        return analysis;
    } else if (accuracy >= 0.7) {
        // Moderate analysis
        let analysis = '';

        if (isWin) {
            analysis = `Good win. You scored ${playerTeamGoals} goals. Keep up this momentum.`;
        } else if (isDraw) {
            analysis = `Draw result. ${playerTeamGoals} goals scored. Could have been better.`;
        } else {
            analysis = `Loss. You conceded ${opponentGoals} goals. Time to reassess your defense.`;
        }

        return analysis;
    } else {
        // Vague analysis
        if (isWin) {
            return `Win secured. Details unclear at this level.`;
        } else if (isDraw) {
            return `Match ended in a draw.`;
        } else {
            return `Unfortunate loss. More insight coming as you improve your assistant manager.`;
        }
    }
};

export const LastResultPanel: React.FC<LastResultPanelProps> = ({ lastFixture, gameState, getPlayerById, pushView, onFixtureClick }) => {
    const { isCompact, toggleView } = usePanelViewPreferences();

    const { homeTeam, awayTeam } = useMemo(() => {
        if (!lastFixture) return { homeTeam: null, awayTeam: null };
        const allTeams = Object.values(gameState.leagues).flatMap((l: League) => l.teams);
        return {
            homeTeam: allTeams.find(t => t.name === lastFixture.home),
            awayTeam: allTeams.find(t => t.name === lastFixture.away)
        };
    }, [lastFixture, gameState.leagues]);

    const readableHomeColor = useMemo(() => {
        if (!homeTeam) return 'var(--text-color)';
        return getThemeReadableAccentColor(homeTeam.colors.primary);
    }, [homeTeam]);

    const readableAwayColor = useMemo(() => {
        if (!awayTeam) return 'var(--text-color)';
        return getThemeReadableAccentColor(awayTeam.colors.primary);
    }, [awayTeam]);

    const teamColorsMap = useMemo(() => {
        const map: Record<string, TeamColors> = {};
        Object.values(gameState.leagues).forEach((league: League) => {
            league.teams.forEach(team => map[team.name] = team.colors);
        });
        return map;
    }, [gameState.leagues]);

    const potmTeamName = lastFixture?.result?.potm?.teamName;
    const readablePotmTeamColor = useMemo(() => {
        if (!potmTeamName || !teamColorsMap[potmTeamName]) return 'var(--text-color)';
        return getThemeReadableAccentColor(teamColorsMap[potmTeamName].primary);
    }, [potmTeamName, teamColorsMap]);

     const potmPlayer = useMemo(() => {
        if (!lastFixture?.result?.potm) return null;
        return getPlayerById(lastFixture.result.potm.playerId);
    }, [lastFixture, getPlayerById]);

    const balanceData = useMemo(() => {
        if (!lastFixture || !lastFixture.result || !homeTeam || !awayTeam) return null;
        const homeStrengths = calculateZoneStrengths(homeTeam, lastFixture.result.homeStartingXI);
        const awayStrengths = calculateZoneStrengths(awayTeam, lastFixture.result.awayStartingXI);
        return { homeColors: homeTeam.colors, awayColors: awayTeam.colors, homeAttack: homeStrengths.attack, homeMid: homeStrengths.mid, homeDef: homeStrengths.def, awayAttack: awayStrengths.attack, awayMid: awayStrengths.mid, awayDef: awayStrengths.def };
    }, [lastFixture, homeTeam, awayTeam]);

    const allPlayersByName = useMemo(() => {
        const map = new Map<string, Player>();
        Object.values(gameState.leagues).flatMap((l: League) => l.teams).flatMap(t => t.squad).forEach(p => map.set(p.name, p));
        return map;
    }, [gameState.leagues]);

    const homeEvents = useMemo(() => {
        if (!lastFixture?.result?.events) return [];
        return lastFixture.result.events.filter(e => e.team === lastFixture.home && (e.type === 'goal' || e.type === 'yellow_card' || e.type === 'red_card')).sort((a,b) => a.minute - b.minute);
    }, [lastFixture]);

    const awayEvents = useMemo(() => {
        if (!lastFixture?.result?.events) return [];
        return lastFixture.result.events.filter(e => e.team === lastFixture.away && (e.type === 'goal' || e.type === 'yellow_card' || e.type === 'red_card')).sort((a,b) => a.minute - b.minute);
    }, [lastFixture]);

    // League positions
    const leaguePositions = useMemo(() => {
        if (!lastFixture) return null;

        // Helper function to find team's league and position
        const getTeamLeagueInfo = (teamName: string) => {
            for (const leagueName in gameState.leagues) {
                const league = gameState.leagues[leagueName];
                const team = league.teams.find(t => t.name === teamName);
                if (team) {
                    const standings = [...league.teams].sort((a, b) => {
                        if (b.points !== a.points) return b.points - a.points;
                        const gdA = a.goalsFor - a.goalsAgainst;
                        const gdB = b.goalsFor - b.goalsAgainst;
                        if (gdB !== gdA) return gdB - gdA;
                        return b.goalsFor - a.goalsFor;
                    });
                    const position = standings.findIndex(t => t.name === teamName) + 1;
                    return { leagueName, position };
                }
            }
            return null;
        };

        const homeInfo = getTeamLeagueInfo(lastFixture.home);
        const awayInfo = getTeamLeagueInfo(lastFixture.away);

        if (!homeInfo || !awayInfo) return null;

        return {
            homePos: homeInfo.position,
            homeLeague: homeInfo.leagueName,
            awayPos: awayInfo.position,
            awayLeague: awayInfo.leagueName,
            isCupMatch: lastFixture.type === 'cup'
        };
    }, [lastFixture, gameState]);

    // Form data for both teams
    const formData = useMemo(() => {
        if (!lastFixture) return null;

        const getTeamForm = (teamName: string) => {
            const allFixtures = FixturesHelper.getAllLeagueFixtures(gameState.fixtures, gameState.playerLeagueName);
            const teamFixtures = allFixtures
                .filter((f: typeof allFixtures[0]) => (f.home === teamName || f.away === teamName) && f.result && f.week < gameState.currentWeek)
                .sort((a: typeof allFixtures[0], b: typeof allFixtures[0]) => b.week - a.week)
                .slice(0, 5);

            const results = teamFixtures.map((f: typeof allFixtures[0]) => {
                const { result, home, away } = f;
                if (!result) return 'D';
                if (result.homeScore === result.awayScore) return 'D';
                return ((home === teamName && result.homeScore > result.awayScore) ||
                        (away === teamName && result.awayScore > result.homeScore)) ? 'W' : 'L';
            }).reverse() as ('W' | 'D' | 'L')[];

            const displayFixtures: DisplayFixture[] = teamFixtures.reverse().map((f: typeof allFixtures[0]) => ({
                ...f,
                competition: gameState.playerLeagueName,
                type: 'league' as const
            }));

            return {
                results,
                fixtures: displayFixtures
            };
        };

        return {
            homeForm: getTeamForm(lastFixture.home),
            awayForm: getTeamForm(lastFixture.away)
        };
    }, [lastFixture, gameState]);

    const handleTeamClick = (e: React.MouseEvent, teamName: string) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName });
    };

    const handlePlayerClick = (e: React.MouseEvent, playerId: number, playerName: string) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${playerName}`, playerId });
    };
    
    const renderEventItem = useCallback((event: MatchEvent) => {
        const playerNameFromGoal = event.message.match(/GOAL! (.*?) scores/)?.[1];
        const player = getPlayerById(
            event.type === 'goal'
            ? (playerNameFromGoal ? allPlayersByName.get(playerNameFromGoal)?.id : undefined) ?? -1
            : (lastFixture!.result?.yellowCards.find(c => c.minute === event.minute)?.playerId ?? lastFixture!.result?.redCards.find(c => c.minute === event.minute)?.playerId) ?? -1
        );
        const playerName = player?.name || event.message.match(/for (.*?) \(/)?.[1] || playerNameFromGoal;
        if (!playerName) return null;
        const icon = event.type === 'goal' ? <FontAwesomeIcon icon={faSoccerBall} style={{ marginRight: '8px' }} /> : event.type === 'yellow_card' ? <span className="event-icon card yellow-card" /> : <span className="event-icon card red-card" />;
        
        return (
            <div key={event.minute + playerName} className="event-item">
                {icon}
                <button className="link-style" onClick={(e) => player && handlePlayerClick(e, player.id, player.name)}>
                    {playerName}
                </button>
                {' '}({event.minute}')
            </div>
        );
    }, [allPlayersByName, getPlayerById, lastFixture, handlePlayerClick]);

    const isCompactView = isCompact('lastResult');

    return (
        <div className="panel dashboard-last-result">
            <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Last Result</span>
                <button
                    onClick={() => toggleView('lastResult')}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        opacity: 0.7,
                        transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
                    title={isCompactView ? 'Show full view' : 'Show compact view'}
                >
                    <FontAwesomeIcon icon={isCompactView ? faExpand : faCompress} />
                </button>
            </div>
            <div className="panel-content">
                {lastFixture && lastFixture.result ? (
                    <div className="clickable-panel-content" onClick={() => onFixtureClick(lastFixture)}>
                        {isCompactView ? (
                            // Compact View
                            <div>
                                <div className="match-card-header">
                                    <div className="match-card-header-meta">{getCompetitionEmoji(lastFixture.competition, gameState)} {lastFixture.competition} - Week {Math.floor(lastFixture.week)}</div>
                                    {leaguePositions && (
                                        <div className="match-card-positions">
                                            {leaguePositions.isCupMatch ? (
                                                <>{leaguePositions.homeLeague} {leaguePositions.homePos}{getOrdinalSuffix(leaguePositions.homePos)} vs {leaguePositions.awayLeague} {leaguePositions.awayPos}{getOrdinalSuffix(leaguePositions.awayPos)}</>
                                            ) : (
                                                <>{leaguePositions.homePos}{getOrdinalSuffix(leaguePositions.homePos)} vs {leaguePositions.awayPos}{getOrdinalSuffix(leaguePositions.awayPos)}</>
                                            )}
                                        </div>
                                    )}
                                    <div className="fixture-teams last-result-teams">
                                        <button className="team-name-button" onClick={(e) => handleTeamClick(e, lastFixture.home)} style={{ color: readableHomeColor }}>
                                            {homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="sm" />}{lastFixture.home}
                                        </button>
                                        <span className={`match-result ${getResultClass(lastFixture, gameState.playerTeamName)}`}>
                                            {lastFixture.result.homeScore} - {lastFixture.result.awayScore}
                                        </span>
                                        <button className="team-name-button" onClick={(e) => handleTeamClick(e, lastFixture.away)} style={{ color: readableAwayColor }}>
                                            {awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="sm" />}{lastFixture.away}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // Full View
                            <div>
                            {/* Header Section */}
                            <div className="match-card-header">
                                <div className="match-card-header-meta">{getCompetitionEmoji(lastFixture.competition, gameState)} {lastFixture.competition} - Week {Math.floor(lastFixture.week)}</div>
                                {leaguePositions && (
                                    <div className="match-card-positions">
                                        {leaguePositions.isCupMatch ? (
                                            <>{leaguePositions.homeLeague} {leaguePositions.homePos}{getOrdinalSuffix(leaguePositions.homePos)} vs {leaguePositions.awayLeague} {leaguePositions.awayPos}{getOrdinalSuffix(leaguePositions.awayPos)}</>
                                        ) : (
                                            <>{leaguePositions.homePos}{getOrdinalSuffix(leaguePositions.homePos)} vs {leaguePositions.awayPos}{getOrdinalSuffix(leaguePositions.awayPos)}</>
                                        )}
                                    </div>
                                )}
                                <div className="fixture-teams last-result-teams">
                                    <button className="team-name-button" onClick={(e) => handleTeamClick(e, lastFixture.home)} style={{ color: readableHomeColor }}>
                                        {homeTeam?.badge && <TeamBadge badge={homeTeam.badge} color={homeTeam.colors.primary} size="sm" />}{lastFixture.home}
                                    </button>
                                    <span className={`match-result ${getResultClass(lastFixture, gameState.playerTeamName)}`}>
                                        {lastFixture.result.homeScore} - {lastFixture.result.awayScore}
                                        {lastFixture.result.penalties && (
                                            <div style={{ fontSize: '0.7em', opacity: 0.8, marginTop: '2px' }}>
                                                (AET {lastFixture.result.penalties.homeScore}-{lastFixture.result.penalties.awayScore} p)
                                            </div>
                                        )}
                                    </span>
                                    <button className="team-name-button" onClick={(e) => handleTeamClick(e, lastFixture.away)} style={{ color: readableAwayColor }}>
                                        {awayTeam?.badge && <TeamBadge badge={awayTeam.badge} color={awayTeam.colors.primary} size="sm" />}{lastFixture.away}
                                    </button>
                                </div>

                                {/* Team Form Section - Immediately under team names */}
                                <div className="match-card-teams-form">
                                    {/* Home Team Form */}
                                    {formData?.homeForm && formData.homeForm.results.length > 0 && (
                                        <div className="team-form-column">
                                            <span className="team-form-label">Form (L5):</span>
                                            <FormDisplay
                                                results={formData.homeForm.results}
                                                fixtures={formData.homeForm.fixtures}
                                                onFixtureClick={onFixtureClick}
                                            />
                                        </div>
                                    )}
                                    {/* Away Team Form */}
                                    {formData?.awayForm && formData.awayForm.results.length > 0 && (
                                        <div className="team-form-column">
                                            <span className="team-form-label">Form (L5):</span>
                                            <FormDisplay
                                                results={formData.awayForm.results}
                                                fixtures={formData.awayForm.fixtures}
                                                onFixtureClick={onFixtureClick}
                                            />
                                        </div>
                                    )}
                                </div>                                
                            </div>

                            {/* Main Info Section */}
                            <div className="match-card-main">
                                {/* Result Box */}
                                <div className="match-card-prediction-section">
                                    <div className="prediction-title">Result</div>
                                    <div className="match-card-prediction">
                                        <strong className={`result-${getResultClass(lastFixture, gameState.playerTeamName)}`}>
                                            {getResultText(lastFixture)}
                                        </strong>
                                    </div>
                                </div>

                                {/* Balance of Play */}
                                {balanceData && <MiniBalanceOfPlay {...balanceData} />}
                            </div>

                            {/* Assistant Manager Analysis Section */}
                            {lastFixture && lastFixture.result && (
                                <div style={{
                                    marginTop: '16px',
                                }}>
                                    <div style={{
                                        backgroundColor: 'var(--secondary-color)',
                                        padding: '12px',
                                        borderRadius: '4px'
                                    }}>
                                        <h4 style={{ color: 'var(--accent-text-color)', margin: '0 0 8px 0', fontSize: '0.95em' }}>
                                            <FontAwesomeIcon icon={faPersonChalkboard} style={{ marginRight: '8px' }} />
                                            Assistant Manager Review
                                        </h4>
                                        <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.85, lineHeight: 1.5 }}>
                                            {homeTeam && (
                                                generateMatchAnalysis(lastFixture, gameState.playerTeamName, ASSISTANT_MANAGER_LEVELS[homeTeam.assistantManager?.level - 1]?.accuracy || 0.6)
                                            )}
                                        </p>
                                    </div>

                                    {/* Accuracy Footer */}
                                    {homeTeam && (
                                        <div style={{
                                            padding: '6px 12px',
                                            marginTop: '8px',
                                            borderTop: '1px solid var(--primary-color)',
                                            fontSize: '0.75em',
                                            opacity: 0.6,
                                            fontStyle: 'italic'
                                        }}>
                                            {(() => {
                                                const assistantLevel = homeTeam?.assistantManager?.level || 1;
                                                const assistantInfo = ASSISTANT_MANAGER_LEVELS[assistantLevel - 1];
                                                const accuracy = assistantInfo?.accuracy || 0.6;
                                                return accuracy < 0.8 ? 'Limited accuracy with current assistant manager level' : 'Detailed analysis with expert assistant manager';
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Details Section */}
                            <div className="match-card-details">
                                {potmPlayer && lastFixture.result?.potm && (
                                    <div className="potm-display">
                                        <div className="potm-header"><FontAwesomeIcon icon={faStar} /> POTM <FontAwesomeIcon icon={faStar} /></div>
                                        <div className="potm-player">
                                            <button className="link-style" onClick={(e) => handlePlayerClick(e, potmPlayer.id, potmPlayer.name)}><PlayerFace player={potmPlayer} teamColor={lastFixture.result?.potm?.teamName ? teamColorsMap[lastFixture.result.potm.teamName]?.primary : undefined} /></button>
                                            <div className="potm-player-info">
                                                <div>
                                                    <button className="link-style" onClick={(e) => handlePlayerClick(e, potmPlayer.id, potmPlayer.name)}><span className="potm-player-name">{potmPlayer.name}</span></button>{' '}
                                                    <button className="link-style" onClick={(e) => handleTeamClick(e, lastFixture.result!.potm!.teamName)}><span className="potm-player-team" style={{ color: readablePotmTeamColor }}>{lastFixture.result.potm.teamName}</span></button>
                                                </div>
                                                <span className="potm-player-rating">{lastFixture.result.potm.rating.toFixed(1)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="match-card-detail-item">
                                    <div className="detail-label">Match Events</div>
                                    <div className="detail-content">
                                        {/* Events/Scorers */}
                                        <div className="last-result-events-list">
                                            <div className="home-events-column">{homeEvents.map(renderEventItem)}</div>
                                            <div className="away-events-column">{awayEvents.map(renderEventItem)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                        )}
                    </div>
                ) : <div style={{ padding: '12px' }}><p>No results from last week.</p></div>}
            </div>
        </div>
    );
};